import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Typography,
  useTheme,
} from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import React, { FC, useEffect, useState } from 'react';
import { useCinemas, useMovieDetailsState } from '@/logic/state';

import { CinemaModel } from '@/logic/services/cinemas';
import CustomizedCircularProgress from '@/components/shared/customized-circular-progress';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { isString } from '@alpha.apps/react-common';
import { pxToRem } from '@/theme/typography';
import shallow from 'zustand/shallow';
import { useCinema } from '@/logic/hooks';
import ExperienceCard from '@/components/experience/experience-card';
import ImageBoxFuture from '@/components/shared/image-box-future';

const LocationIcon = '/icons/shared/location-icon-blue.svg';

interface ExperienceCollapseListProps {
  movieId: number;
  cinemas: CinemaModel[];
  loadTheLeftCinemas: () => void;
  callOnFilmHasBeenStarted: () => void;
  showTimeDate?: string;
}
export const ExperienceCollapseList: FC<ExperienceCollapseListProps> = ({
  movieId,
  cinemas,
  loadTheLeftCinemas,
  callOnFilmHasBeenStarted,
  showTimeDate,
}) => {
  const theme = useTheme();
  const intl = useIntl();
  const [activeAccordion, setExpanded] = useState<number | null>(0);
  const selectedCinemasHomePage = useCinemas(s => s.selectedCinemas, shallow);
  const { selectedCinemas } = useCinema();

  const [isFetching, isFilterMode, showMoreBtn] = useMovieDetailsState(
    s => [s.isFetching, s.isFilterMode, s.showMoreBtn],
    shallow
  );

  const handleChange = (panelNumber: number) => () => {
    if (activeAccordion === panelNumber) setExpanded(null);
    else setExpanded(panelNumber);
  };

  const checkIsAllCinemasSelected = (): boolean => {
    // 0 is the is of all selected cinemas
    const index = selectedCinemasHomePage.findIndex(id => id === 0);
    return index !== -1;
  };
  useEffect(() => {
    if (isString(showTimeDate)) {
      setExpanded(0);
    }
  }, [showTimeDate]);
  return (
    <Box
      sx={{
        paddingInline: pxToRem(30),
      }}
    >
      <Box>
        {!isFetching &&
          cinemas?.map((cinema: CinemaModel, index: number) => (
            <Accordion
              key={cinema.id}
              expanded={activeAccordion === index}
              onChange={handleChange(index)}
              sx={{
                marginTop: pxToRem(40),
              }}
            >
              <AccordionSummary
                expandIcon={
                  <ExpandMoreIcon sx={{ color: theme.palette.secondary.main }} />
                }
                id={`cinema-collapse-${index}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <Box
                  component='div'
                  sx={{
                    width: pxToRem(36),
                    height: pxToRem(36),
                    backgroundColor: theme.palette.common.white,
                    borderRadius: '9px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginInlineEnd: '16px',
                  }}
                >
                  <ImageBoxFuture
                    img={LocationIcon}
                    title={intl.formatMessage({
                      id: 'images.title.location',
                    })}
                    objectFit='contain'
                    sx={{
                      width: '15px',
                      height: '18px',
                    }}
                  />
                </Box>
                <Typography
                  sx={{
                    color: theme.palette.grey[80],
                    fontWeight: 400,
                    fontSize: pxToRem(20),
                    lineHeight: '30px',
                  }}
                >
                  {cinema.name}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                {cinema.experiences?.map((experience, index: number) => (
                  <ExperienceCard
                    key={experience.id}
                    movieId={movieId}
                    experience={experience}
                    index={index}
                    callOnFilmHasBeenStarted={callOnFilmHasBeenStarted}
                  />
                ))}
              </AccordionDetails>
            </Accordion>
          ))}
        {cinemas?.length === 0 && !isFetching && (
          <>
            <Box
              sx={{
                marginTop: pxToRem(80),
                marginBottom: pxToRem(24),
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <ImageBoxFuture
                img='/icons/movie-details/no-showtimes-available.svg'
                title={intl.formatMessage({
                  id: 'alt.no_show_time_available',
                })}
                objectFit='contain'
                sx={{
                  width: '382px',
                  height: '282px',
                }}
              />
            </Box>
            <Typography
              sx={{
                color: theme.palette.grey[50],
                fontWeight: 400,
                fontSize: pxToRem(20),
                lineHeight: '30px',
                textAlign: 'center',
              }}
            >
              <FormattedMessage id='movies.there.is.no.showtimes' />
            </Typography>
          </>
        )}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: pxToRem(52),
          }}
        >
          {showMoreBtn &&
            !isFetching &&
            !isFilterMode &&
            selectedCinemas.length !== 0 &&
            !checkIsAllCinemasSelected() && (
              <Button
                variant='text'
                fullWidth={false}
                sx={{
                  fontSize: pxToRem(16),
                  fontWeight: '700',
                  color: theme.palette.grey[70],
                  marginTop: pxToRem(58),
                }}
                onClick={() => loadTheLeftCinemas()}
              >
                <FormattedMessage id='movie_details.show.more.cinemas' />
              </Button>
            )}
          {isFetching && <CustomizedCircularProgress size={40} thickness={4} />}
        </Box>
      </Box>
    </Box>
  );
};

export default ExperienceCollapseList;
